import jsPDF from "jspdf";

import moment from "moment";
import { Attachment, Correspondance, QuoteReportProps, QuoteSliceInterface } from "../types";
import { fetchImageForJSPDF, formatCurrency, getImageBase64, hexToGrayscale, pxToInch } from "./helpers";

interface iTableData { rows: any[], columns: any[], header_color?: string }

const generateData = (tableData: iTableData) => {
  const rows = tableData.rows?.map((row: any) => {
    let object = {};
    tableData?.columns?.forEach((col: any) => {
      const content =
        row?.column_content?.find(
          (content: any) => content?.field === col?.field
        )?.content || "";
      object[col.field] = content;
    });
    return object;
  });
  return rows;
};

const addSubHeaders = ({ doc, name, value = "", yPos, header_color = "", xEndPos = 0, size = 12, enableBold = false }: {
  doc: jsPDF; name: string; value?: string; yPos: number; header_color?: string; xEndPos?: number; size?: number; enableBold?: boolean;
}) => {

  // Set up the page width and font size
  const pageWidth = doc.internal.pageSize.getWidth();
  const fontSize = size;
  const textHeight = fontSize / 2;
  const padding = 3;

  doc.setFontSize(fontSize);

  // Left-aligned text
  const leftText = name;

  // Right-aligned text (value)
  const rightText = value;

  // X position for the left-aligned text (start from the left)
  const leftX = 7;

  // Measure the width of the right-aligned text
  const rightTextWidth = doc.getTextWidth(rightText);

  // X position for the right-aligned text (start from the right, subtract the text width)
  const rightX = (xEndPos || pageWidth) - 10 - rightTextWidth;

  // Y position for both (on the same line)
  let yPosition = yPos;

  if (header_color) {
    // Draw background rectangle
    doc.setFillColor(header_color); // Background color
    doc.rect(leftX, yPosition - textHeight, (xEndPos || doc.internal.pageSize.getWidth()) - 7, textHeight + padding, "F");

    // Draw text
    if (header_color && hexToGrayscale(header_color) > 150) {
      doc.setTextColor(0, 0, 0); // Text color
    } else {
      doc.setTextColor(255, 255, 255); // Text color
    }
  }

  // Render the left-aligned text
  if (enableBold) {
    doc.setFont(undefined, "bold");
  }
  doc.text(leftText, leftX + 1, yPosition);

  // Render the right-aligned text
  if (enableBold) {
    doc.setFont(undefined, "normal");
  }
  doc.text(rightText, rightX, yPosition - 1);

  return textHeight + 2;
};

const addTotalText = (doc: jsPDF, name: string, value: string, yPos: number, header_color: string = "", xEndPos: number = 0) => {
  // Set up the page width and font size
  const pageWidth = doc.internal.pageSize.getWidth();
  const fontSize = 12;
  const textHeight = fontSize / 2;
  const padding = 3;

  doc.setFontSize(fontSize);

  // Left-aligned text
  const leftText = name;

  // Right-aligned text (value)
  const rightText = value;

  // X position for the left-aligned text (start from the left)
  const leftX = 7;

  // Measure the width of the right-aligned text
  const rightTextWidth = doc.getTextWidth(rightText);

  // X position for the right-aligned text (start from the right, subtract the text width)
  const rightX = (xEndPos || pageWidth) - 10 - rightTextWidth;

  // Y position for both (on the same line)
  let yPosition = yPos;

  if (header_color) {
    // Draw background rectangle
    doc.setFillColor(header_color); // Background color
    doc.rect(leftX, yPosition - textHeight, (xEndPos || doc.internal.pageSize.getWidth()) - 7, textHeight + padding, "F");

    // Draw text
    if (header_color && hexToGrayscale(header_color) > 150) {
      doc.setTextColor(0, 0, 0); // Text color
    } else {
      doc.setTextColor(255, 255, 255); // Text color
    }
  }

  // Render the left-aligned text
  doc.text(leftText, leftX + 1, yPosition);

  // Render the right-aligned text
  doc.text(rightText, rightX, yPosition);

  doc.line(
    leftX,
    yPosition + 2,
    xEndPos || doc.internal.pageSize.getWidth(),
    yPosition + 2
  ); // Draw the line below the text

  return textHeight + 6;
};

const drawTable = (doc: any, tableData: iTableData, startY: number, hideHeader: boolean, label: string = "") => {
  const totalWidth = tableData.columns.reduce(
    (sum, col) => sum + (col.width || 150),
    0
  );

  let tableHeight = 0;

  // Text position and dimensions
  const x = 7;
  const fontSize = 12;

  const titleHeight = fontSize / 2;

  // Calculate the starting Y position for the table
  const tableStartY = startY;

  doc.autoTable({
    startY: tableStartY,
    head: hideHeader ? [] : [tableData.columns.map((col) => col.header_name)],
    body: generateData(tableData),

    columns: tableData.columns.map((col) => ({
      dataKey: col.field,
      header: hideHeader ? "" : col.header_name,
    })),

    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: "linebreak",
    },

    headStyles: {
      fillColor: tableData.header_color,
      textColor: `${hexToGrayscale(tableData.header_color) > 150 ? "#000000" : "#FFFFFF"
        }`,
    },

    columnStyles: tableData.columns.reduce((acc, col, index) => {
      acc[col.field] = { cellWidth: pxToInch(col.width || 150) };
      return acc;
    }, {}),

    didDrawCell: (data) => {
      if (data.cell.section === "body" || data.cell.section === "head") {
        const { doc, cell } = data;
        doc.setDrawColor(128, 128, 128); // Set border color to grey (RGB: 128, 128, 128)
      }
    },

    didDrawPage: (data) => {
      // This will be called after each page is drawn
      tableHeight = data.cursor.y - tableStartY;
    },

    tableWidth: pxToInch(totalWidth),
    margin: { left: x, right: x },
  });

  // Calculate and return the total height (text + table)
  const totalHeight = tableHeight + Math.max(titleHeight) + 5;
  const lastColWidth =
    doc?.lastAutoTable?.columns?.[doc?.lastAutoTable?.columns?.length - 1]
      ?.width ?? 0;
  const lastColX =
    doc?.lastAutoTable?.head?.[0]?.cells?.[
      tableData.columns?.[tableData.columns?.length - 1]?.field
    ]?.x ?? 0;

  return { y: startY + totalHeight, x: lastColWidth + lastColX };
};

const calculateAddressHeight = (doc: jsPDF, correspondance: Correspondance, fontSize: number, pageWidth: number, drawLine: boolean): number => {

  const { soldBy, soldTo, shipFrom, shipTo } = correspondance;
  const address = [soldBy, soldTo, shipFrom, shipTo].filter((s) => s.include);

  const colWidth = (pageWidth - 10) / address.length - 5;

  doc.setFontSize(fontSize);

  let defaultY = drawLine ? 10 : 0;
  let max = defaultY;
  let addY = defaultY;

  for (const addressType of address) {
    const { name, emailId, phoneNumber, address } = addressType.address;

    addY = defaultY;
    addY += fontSize / 2;

    if (name?.value) {
      const wrappedText = doc.splitTextToSize(name.value?.trim(), colWidth - 5);
      wrappedText?.forEach((_) => {
        addY += fontSize / 2;
      });
    };

    if (address?.value) {
      const wrappedText = doc.splitTextToSize(address.value?.trim(), colWidth - 5);
      wrappedText?.forEach((_) => {
        addY += fontSize / 2;
      });
    };

    if (emailId?.value) {
      const wrappedText = doc.splitTextToSize(emailId.value?.trim(), colWidth - 5);
      wrappedText?.forEach((_) => {
        addY += fontSize / 2;
      });
    };

    if (phoneNumber?.value) {
      const wrappedText = doc.splitTextToSize(phoneNumber.value?.trim(), colWidth - 5);
      wrappedText?.forEach((_) => {
        addY += fontSize / 2;
      });
    };

    max = Math.max(max, addY);
  }

  return max + 10;
};

const renderAddress = ({ doc, correspondance, currentY, drawLine = false }: {
  doc: jsPDF; correspondance: Correspondance; currentY: number; drawLine?: boolean;
}) => {

  const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
  const { soldBy, soldTo, shipFrom, shipTo } = correspondance;
  const address = [soldBy, soldTo, shipFrom, shipTo].filter((s) => s.include);
  const colWidth = (pageWidth - 10) / address.length - 5;

  const options = { maxWidth: colWidth };

  let x = 7;

  let defaultY = currentY + (drawLine ? 10 : 5);
  let addY = defaultY;
  let max = defaultY;

  if (address.length > 0) {
    if (drawLine) {
      // Set the draw color for the line (RGB)
      doc.setDrawColor(211, 211, 211); // Red color (use RGB values)

      // Draw a line (x1, y1, x2, y2)
      doc.line(5, currentY, pageWidth - 5, currentY); // 1 unit below the text
    }

    let fontSize = 9;

    if (soldBy.include) {

      x += 1;

      const { name, emailId, phoneNumber, address } = soldBy.address;

      doc.setFontSize(fontSize);
      doc.setFont(undefined, "bold");
      doc.text("Quoted By: ", x, addY, options);
      doc.setFont(undefined, "normal");
      addY += fontSize / 2;
      addY += 2;

      if (name.include) {
        const wrappedText = doc.splitTextToSize(name.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (address.include) {
        const wrappedText = doc.splitTextToSize(address.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (emailId.include) {
        const wrappedText = doc.splitTextToSize(emailId.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (phoneNumber.include) {
        const wrappedText = doc.splitTextToSize(phoneNumber.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      x += colWidth;

      max = Math.max(max, addY);
      addY = defaultY;
    }

    if (soldTo.include) {

      const { name, emailId, phoneNumber, address } = soldTo.address;

      doc.setFontSize(fontSize);
      doc.setFont(undefined, "bold");
      doc.text("Quoted To: ", x, addY, options);
      doc.setFont(undefined, "normal");
      addY += fontSize / 2;
      addY += 2;

      if (name.include) {
        const wrappedText = doc.splitTextToSize(name.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (address.include) {
        const wrappedText = doc.splitTextToSize(address.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (emailId.include) {
        const wrappedText = doc.splitTextToSize(emailId.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (phoneNumber.include) {
        const wrappedText = doc.splitTextToSize(phoneNumber.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      x += colWidth;

      max = Math.max(max, addY);
      addY = defaultY;
    };

    if (shipFrom.include) {

      const { name, emailId, phoneNumber, address } = shipFrom.address;

      doc.setFontSize(fontSize);
      doc.setFont(undefined, "bold");
      doc.text("Shipped From: ", x, addY, options);
      doc.setFont(undefined, "normal");
      addY += fontSize / 2;
      addY += 2;

      if (name.include) {
        const wrappedText = doc.splitTextToSize(name.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (address.include) {
        const wrappedText = doc.splitTextToSize(address.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (emailId.include) {
        const wrappedText = doc.splitTextToSize(emailId.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (phoneNumber.include) {
        const wrappedText = doc.splitTextToSize(phoneNumber.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      max = Math.max(max, addY);

      x += colWidth;

      addY = defaultY;
    };

    if (shipTo.include) {

      const { name, emailId, phoneNumber, address } = shipTo.address;

      doc.setFontSize(fontSize);
      doc.setFont(undefined, "bold");
      doc.text("Shipped To: ", x, addY, options);
      doc.setFont(undefined, "normal");
      addY += fontSize / 2;
      addY += 2;

      if (name.include) {
        const wrappedText = doc.splitTextToSize(name.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (address.include) {
        const wrappedText = doc.splitTextToSize(address.value?.trim(), colWidth);
        doc.text(wrappedText, x, addY, options);
        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (emailId.include) {
        const wrappedText = doc.splitTextToSize(emailId.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      if (phoneNumber.include) {
        const wrappedText = doc.splitTextToSize(phoneNumber.value?.trim(), colWidth - 5);
        doc.text(wrappedText, x, addY, options);

        wrappedText?.forEach((_) => {
          addY += fontSize / 2;
        });
      };

      x += colWidth;

      max = Math.max(max, addY);
    };

    currentY = max;
  }

  return currentY;
};

export const generateCoverPage = async ({ doc, quote, currentY, logoData, id, date }: {
  doc: jsPDF; quote: QuoteSliceInterface; currentY: number;
  logoData: {
    fileDetails: Attachment | File | null;
    url: string;
  }; id: string; date: string;
}): Promise<number> => {
  return new Promise<number>(async (resolve, reject) => {

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const { cover_page_config } = quote;

    currentY += addSubHeaders({ doc, name: id, value: date, yPos: currentY, size: 10, });
    currentY += pageHeight * 0.15;

    const cover_page_header = cover_page_config.heading.trim();

    if (cover_page_config.include_heading && cover_page_header) {

      const fontSize = 16;
      let y = currentY;

      // Set the font size to calculate the width and height of the text
      doc.setFontSize(fontSize);

      // Split the text into an array of lines
      const wrappedText = doc.splitTextToSize(cover_page_header, pageWidth - 40);

      // Loop through each line and center it
      wrappedText.forEach((line) => {
        // Calculate the x position to center the line
        const lineWidth = doc.getTextWidth(line);
        const x = (pageWidth - lineWidth) / 2;

        // Add the line to the PDF
        doc.text(line, x, y);

        // Move to the next line
        y += fontSize / 2;
      });

      currentY = y;
    };

    if (cover_page_config.logo.isActive && logoData.url) {

      const fileType = ((logoData.fileDetails as Attachment)?.file_name ?? (logoData.fileDetails as File)?.name ?? logoData.url).split(".").pop();

      if (fileType) {

        const imageWidth = cover_page_config?.logo?.width || 100;
        const imageHeight = cover_page_config?.logo?.height || 100;
        let x = +cover_page_config.logo.horizontal;
        let y = +cover_page_config.logo.vertical;

        if (logoData.url === "/imageNotFound.jpg") {
          x = (pageWidth - imageWidth) / 2;
          y = (pageHeight - imageHeight) / 2;
        };

        try {
          const imageURL = await fetchImageForJSPDF(logoData.url);
          doc.addImage(imageURL, fileType, x, y, imageWidth, imageHeight);
          currentY += imageHeight + 10;
        } catch (error) {
          console.error("Unable to load the image", JSON.stringify(error));
        }
      }
    }

    const addressHeight = calculateAddressHeight(doc, quote.correspondance, 9, pageWidth, true);

    const footerStartY = pageHeight - addressHeight; // Add some padding at the bottom

    currentY = renderAddress({
      doc, correspondance: quote.correspondance, currentY: footerStartY, drawLine: true,
    });

    currentY += 10;

    resolve(currentY);
  });
};

export const generateQuoteReport = async ({ title, quoteDetails, configState, ...rest }: QuoteReportProps) => {

  const addSubHeader = (doc: jsPDF, title: string, yPos: number, fontSize: number = 12) => {
    // Text position and dimensions
    const x = 8;
    let y = yPos;
    let textHeight = fontSize / 2;

    // Set the font size to calculate the width and height of the text
    doc.setFontSize(fontSize);

    // Draw text
    doc.setTextColor(0, 0, 0); // Text color

    const pageWidth = doc.internal.pageSize.getWidth();

    const wrappedText = doc.splitTextToSize(title, pageWidth - 20);

    wrappedText.forEach((text) => {
      // If adding this line would go beyond the page height, add a new page
      if (y + textHeight > pageHeight - (textHeight + 2)) {
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        y = 13; // Reset y position for the new page
      }

      doc.setFont(undefined, "bold");
      doc.text(text, x, y);
      y += textHeight;
    });

    wrappedText?.slice(1)?.forEach((_) => {
      textHeight += fontSize / 2;
    });

    doc.setFont(undefined, "normal");

    return textHeight + 4;
  };

  const addText = (doc: jsPDF, content: string, yPos: number, fontSize: number = 10) => {
    // Text position and dimensions
    const x = 8;
    let y = yPos;
    let textHeight = fontSize / 2;

    // Set the font size to calculate the width and height of the text
    doc.setFontSize(fontSize);

    // Draw text
    doc.setTextColor(0, 0, 0); // Text color

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const wrappedText: string[] = doc.splitTextToSize(content, pageWidth - 20);

    wrappedText.forEach((text) => {
      // If adding this line would go beyond the page height, add a new page
      if (y + textHeight > pageHeight - (textHeight + 2)) {
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        y = 13; // Reset y position for the new page
      }

      // Set the font size to calculate the width and height of the text
      doc.setFontSize(fontSize);

      doc.setFont(undefined, "normal");
      doc.text(text, x, y);
      y += textHeight;
    });

    wrappedText?.slice(1)?.forEach((_) => {
      textHeight += fontSize / 2;
    });

    return textHeight;
  };

  const base_currency = quoteDetails?.currency?.value ?? "USD";

  const header_color = configState?.tableHeaderColor || "#d1c4e9";
  const useMBFormatter = configState?.useMBFormatter ?? false;
  const include_serial_num = configState?.include_serial_num ?? false;

  let ID = `Quote ID: ${quoteDetails.sequence_id} | Rev: ${quoteDetails.version}`;
  const DATE = `Date: ${moment(quoteDetails.creationDate).format("DD-MMM-YYYY")}`;

  if (include_serial_num) {
    ID = `Custom Reference No: ${quoteDetails?.serial_num ?? "-"}`;
  }

  let doc = new jsPDF({
    putOnlyUsedFonts: true,
    orientation: rest.orientation ?? "portrait",
    format: rest.paperSize,
  });

  const pageHeight = doc.internal.pageSize.getHeight(); // Get page height
  const pageWidth = doc.internal.pageSize.getWidth(); // Get page width
  const footerYPosition = pageHeight - 2; // Fixed position for footer

  // Set document properties
  doc.setProperties({ title: title.trim() });

  let currentY = 13;

  if (quoteDetails.cover_page_config.include_cover_page) {

    const cover_page_config = quoteDetails.cover_page_config;

    let url = ((cover_page_config?.logo?.src as Attachment)?.url || cover_page_config?.attachments?.[0]?.url) as string;

    const logoData = {
      fileDetails:
        quoteDetails.cover_page_config.logo.src ||
        cover_page_config?.attachments?.[0],
      url,
    };

    currentY += await generateCoverPage({ doc, quote: quoteDetails, currentY: currentY, logoData, id: ID, date: DATE });

    doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);

    currentY = 13; // Reset y position for the new page
  } else {
    currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
    currentY = renderAddress({ doc, correspondance: quoteDetails.correspondance, currentY });
  }

  let tableEndX = 0;

  if (quoteDetails.cover_letter.include) {

    if (quoteDetails.cover_page_config.include_cover_page) {
      currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
    }

    currentY += 10;

    if (quoteDetails.include_cover_letter_from) {

      const fromAddress = quoteDetails.cover_letter.fromAddress?.trim();

      currentY += addSubHeaders({ doc, name: `From`, yPos: currentY, size: 9, enableBold: true });

      if (fromAddress) {
        currentY += addText(doc, fromAddress ?? "", currentY);
        currentY += 2.5;
      }
    }

    const letter = quoteDetails.cover_letter;

    if (letter.subject?.trim() && quoteDetails.include_cover_letter_subject) {
      currentY += addSubHeaders({ doc, name: `Subject: ${(letter.subject ?? "")?.trim()}`, yPos: currentY, enableBold: true, size: 9 });
    }

    if (letter.content?.trim()) {
      currentY += addText(doc, (letter.content ?? "")?.trim(), currentY);
      currentY += 5;
    }

    if (letter.regards?.trim()) {
      currentY += addSubHeaders({ doc, name: (letter.regards ?? "")?.trim(), yPos: currentY, size: 9, enableBold: true });
    }

    if (letter.attachments?.length) {

      let contentUrl = letter.attachments?.[0]?.url;

      await fetchImageForJSPDF(contentUrl).then((contentBytes: string) => {
        doc.addImage(contentBytes, `${contentUrl?.split(".")?.at(-1)?.toUpperCase() ?? ""}`, 8, currentY - 3, 30, 10);
        currentY = currentY + 12;
      }).catch(async (error: any) => {
        console.error(`Error loading content image on preview: ${error}`);
      });
    }

    if (letter.name?.trim()) {
      currentY += addText(doc, (letter.name ?? "")?.trim(), currentY, 9);
    }

    if (letter.designation?.trim()) {
      currentY += addText(doc, (letter.designation ?? "")?.trim(), currentY, 9);
    }

    doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
    currentY = 13;

    currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
    currentY += 5;

  } else {
    if (quoteDetails.cover_page_config.include_cover_page) {
      currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
    }
    currentY += 5;
  }

  const contents = quoteDetails.contents?.filter((t) => t.heading?.trim() && t.notes?.trim()) ?? [];

  if (contents.length > 0) {

    contents?.forEach((content, idx) => {
      if (currentY >= doc.internal.pageSize.getHeight() - 70 && idx < contents?.length - 1) {

        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        currentY = 13;

        currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
      }

      if (idx > 0) {
        currentY += 5;
      }

      currentY += addSubHeader(doc, `${idx + 1}. ${content.heading?.trim()}`, currentY, 10);

      currentY += addText(doc, content.notes?.trim(), currentY, 8);

      if (currentY >= doc.internal.pageSize.getHeight() - 70 && idx < contents?.length - 1) {
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        currentY = 13;
        currentY += addSubHeaders({ doc, name: ID, value: DATE, yPos: currentY, size: 10 });
      }
    });

    currentY += 5;
  }

  currentY += addSubHeaders({
    doc, name: `List of Parts included in the quote`, yPos: currentY, enableBold: true,
  });

  const costBreakupCols = [
    { header_name: "Part/Item No.", field: "partNumber", width: 90 },
    { header_name: "Description", field: "description", width: 150 },
    { header_name: "Unit Price", field: "unitCost", width: 80 },
    { header_name: "Discount(%)", field: "discount", width: 80 },
    { header_name: "Quantity", field: "qty", width: 80 },
    {
      header_name: `Sale Price (${base_currency})`,
      field: "totalCost",
      width: 160,
    },
    { header_name: "Delivery Time", field: "leadTime", width: 100 },
  ];

  let quoteRows = [];

  if (quoteDetails.customQuote) {
    quoteRows = quoteDetails.quote_custom_item_details?.map((q, idx) => {
      return {
        column_content: [
          { field: "partNumber", content: (q?.part_number ?? "")?.trim() },
          { field: "description", content: (q?.description ?? "")?.trim() },
          {
            field: "unitCost",
            content: formatCurrency(q.unitCost ?? 0, base_currency, useMBFormatter),
          },
          { field: "discount", content: q.discount ?? 0 },
          { field: "qty", content: q.quantity ?? 1 },
          {
            field: "totalCost",
            content: formatCurrency(q.totalCost ?? 0, base_currency, useMBFormatter),
          },
          { field: "leadTime", content: q.leadTime?.trim() },
        ],
        id: idx + 1,
      };
    });
  } else {
    quoteRows = quoteDetails.quote_item_details?.map((q, idx) => {
      return {
        column_content: [
          { field: "partNumber", content: (q.partNumber ?? "")?.trim() },
          { field: "description", content: (q?.description ?? "")?.trim() },
          {
            field: "unitCost",
            content: formatCurrency(q.unitCost ?? 0, base_currency, useMBFormatter),
          },
          { field: "discount", content: q.discount ?? 0 },
          { field: "qty", content: q.quantity ?? 1 },
          {
            field: "totalCost",
            content: formatCurrency(q.totalCost ?? 0, base_currency, useMBFormatter),
          },
          { field: "leadTime", content: q.leadTime?.trim() },
        ],
        id: idx + 1,
      };
    });
  }

  const quoteTable = {
    header_color,
    columns: costBreakupCols,
    rows: quoteRows,
  };

  let pos = drawTable(doc, quoteTable, currentY, false);

  tableEndX = pos.x;

  currentY = pos.y;

  const tax_details = quoteDetails?.tax_details?.filter((t) => t?.value) ?? [];

  if (tax_details?.length > 0) {
    const taxCols = [
      { header_name: "Part/Item No.", field: "partNumber", width: 100 },
      { header_name: "Description", field: "description", width: 150 },
      { header_name: "Unit Price", field: "unitCost", width: 80 },
      { header_name: "Discount(%)", field: "discount", width: 80 },
      { header_name: "Quantity", field: "qty", width: 80 },
      {
        header_name: `Sale Price (${base_currency})`,
        field: "totalCost",
        width: 160,
      },
      { header_name: "Delivery Time", field: "leadTime", width: 100 },
    ];

    currentY += addTotalText(
      doc,
      "Total Cost (before tax)",
      formatCurrency(quoteDetails?.total_quote_item_price ?? 0, base_currency, useMBFormatter),
      currentY,
      header_color,
      tableEndX
    );

    currentY += addSubHeaders({
      doc,
      name: `Taxes`,
      yPos: currentY,
      enableBold: true,
    });

    let taxRows = tax_details?.map((q, idx) => {
      return {
        column_content: [
          { field: "partNumber", content: "N/A" },
          { field: "description", content: (q?.description ?? "")?.trim() },
          {
            field: "unitCost",
            content: "N/A",
          },
          { field: "discount", content: "N/A" },
          { field: "qty", content: `${q?.value ?? 0}%` },
          {
            field: "totalCost",
            content: "N/A",
          },
          { field: "leadTime", content: "N/A" },
        ],
        id: idx + 1,
      };
    });

    const taxTable = {
      header_color,
      columns: taxCols,
      rows: taxRows,
    };

    let pos = drawTable(doc, taxTable, currentY, true);

    // console.log("pos", pos)

    // tableEndX = pos.x;

    currentY = pos.y;

    currentY += addTotalText(
      doc,
      `Total Tax`,
      formatCurrency(quoteDetails?.total_quote_tax_price ?? 0, base_currency, useMBFormatter),
      currentY,
      "",
      tableEndX
    );

    currentY += addTotalText(
      doc,
      "Total Cost (after tax)",
      formatCurrency(quoteDetails?.total_quote_price ?? 0, base_currency, useMBFormatter),
      currentY,
      header_color,
      tableEndX
    );
  } else {
    currentY += addTotalText(
      doc,
      "Total Cost",
      formatCurrency(quoteDetails?.total_quote_price ?? 0, base_currency, useMBFormatter),
      currentY,
      header_color,
      tableEndX
    );
  }

  const terms_and_conditions =
    quoteDetails.terms_and_conditions?.filter(
      (t) => t.heading?.trim() && t.notes?.trim()
    ) ?? [];

  if (terms_and_conditions.length > 0) {
    terms_and_conditions?.forEach((t, idx) => {
      if (
        currentY >= doc.internal.pageSize.getHeight() - 70 &&
        idx < terms_and_conditions?.length - 1
      ) {
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        currentY = 13;

        currentY += addSubHeaders({
          doc,
          name: ID,
          value: DATE,
          yPos: currentY,
          size: 10,
        });
      }

      if (idx === 0) {
        currentY += addSubHeaders({
          doc,
          name: `Terms & Conditions`,
          yPos: currentY,
          enableBold: true,
        });
      }

      if (idx > 0) {
        currentY += 5;
      }

      currentY += addSubHeader(
        doc,
        `${idx + 1}. ${t.heading?.trim()}`,
        currentY,
        10
      );

      currentY += addText(doc, t.notes?.trim(), currentY, 8);

      if (
        currentY >= doc.internal.pageSize.getHeight() - 70 &&
        idx < terms_and_conditions?.length - 1
      ) {
        doc.addPage(rest.orientation ?? "portrait", rest.paperSize as any);
        currentY = 13;

        currentY += addSubHeaders({
          doc,
          name: ID,
          value: DATE,
          yPos: currentY,
          size: 10,
        });
      }
    });
  }

  if (rest.showFooter) {
    // Now ensure consistent footer on all pages
    const totalPages = doc.getNumberOfPages(); // Get total number of pages

    // Loop through all pages to add footer at the same Y position
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i); // Set focus on page 'i'

      // Footer logic
      const footerText = `Generated using IEHUB on ${moment(new Date()).format(
        "ll"
      )}`;
      doc.setFontSize(7);
      doc.setTextColor(125, 125, 125); // Gray text color
      const textWidth = doc.getTextWidth(footerText);

      // Draw footer at the fixed bottom-right of each page
      if (rest.footerURL) {
        doc.textWithLink(
          footerText,
          pageWidth - textWidth - 10,
          footerYPosition,
          { url: rest.footerURL }
        );
      } else {
        doc.text(footerText, pageWidth - textWidth - 10, footerYPosition);
      }
    }
  }

  return doc.output("datauristring"); // base64 string
};
